import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';

// Cluster | Sequential | Parallel | Total | actions
const GRID_COLUMNS = '2fr 1fr 1fr 1.2fr 1.4fr';

export const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
`;

export const GridHeader = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 20px;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 2px solid #e2e8f0;

  span {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    white-space: nowrap;

    &.num {
      text-align: right;
    }

    &.help {
      cursor: help;
      text-decoration: underline dotted #CBD5E1;
      text-underline-offset: 3px;
    }
  }
`;

export const PolicyRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 20px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;

  &:hover {
    background-color: #f8fafc;
  }

  /* Clusters are a tree, and a child inherits its parent's policy
     (ADR-095 §3). The indent is what makes that inheritance legible — a flat
     list is why a company-wide default could be set unnoticed.
     It is applied to the NAME, not to the row: indenting the row shrinks its
     grid, so every number column drifted right by one level of depth and the
     table stopped lining up with its own header. */
  .unit {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: ${({ $depth }) => ($depth || 0) * 22}px;
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    min-width: 0;
  }

  /* Only a configured cluster expands (ADR-096 §5). The placeholder
     keeps every name on the same left edge, so the column does not ripple. */
  .chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    font-size: 11px;
    color: #94A3B8;
    cursor: pointer;
    flex-shrink: 0;

    &.placeholder {
      cursor: default;
    }
  }

  /* An inheriting cluster is not a to-do at all: it IS measured, just
     not by a rule of its own. Distinct from the "No policy" tag so the two
     states never read as the same thing. */
  .tag.inherited {
    background: #EFF6FF;
    border-color: #BFDBFE;
    color: #3B6FB0;
    text-transform: none;
    letter-spacing: 0;
  }

  /* An unconfigured cluster is a to-do, not an error — muted, not red */
  .tag {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #64748B;
    background: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 1px 8px;
    white-space: nowrap;
    cursor: help;
  }

  .count {
    max-width: 90px;
    justify-self: end;
  }

  .total {
    text-align: right;
    font-size: 14px;
    font-weight: 700;
    color: #334155;

    &.muted {
      color: #94A3B8;
      font-weight: 400;
    }
  }
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

export const Hint = styled.div`
  font-size: 12px;
  color: #94A3B8;

  &.footer {
    margin-top: 16px;
    padding: 10px 16px;
    border-top: 1px dashed #e2e8f0;
    line-height: 1.6;
  }
`;

// ADR-096: a cluster's position overrides, indented beneath it.
//
// Quieter than the policy row and sharing its column template, so an override
// reads as a modification of the row above rather than a row of its own — the
// same relationship the schema encodes with the foreign key.
export const OverrideBlock = styled.div`
  background: #FAFBFC;
  border-bottom: 1px solid #f1f5f9;
  padding: 6px 0 10px;

  /* The block hangs off ITS cluster, so it carries that row's depth
     (ADR-097 §5). Published as a variable rather than baked into each rule
     because only the first column may move — the numbers underneath belong to
     the header's columns no matter how deep their cluster sits. */
  --indent: ${({ $depth }) => ($depth || 0) * 22}px;

  .empty {
    font-size: 12px;
    color: #94A3B8;
    padding: 8px 16px 8px calc(46px + var(--indent, 0px));
  }
`;

export const OverrideRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 20px;
  align-items: center;
  padding: 4px 16px;

  .position {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: calc(30px + var(--indent, 0px));
    font-size: 13px;
    color: #334155;
    min-width: 0;

    /* Marks the row as belonging to the cluster above it */
    &::before {
      content: '↳';
      color: #CBD5E1;
      flex-shrink: 0;
    }
  }

  .num {
    text-align: right;
    font-size: 13px;
    color: #334155;
    /* A saved number is data: same-width digits, so a column of rules scans
       vertically and never reads as text typed into a box. */
    font-variant-numeric: tabular-nums;
    font-weight: 600;

    &.total {
      font-weight: 600;
    }

    &.muted {
      color: #CBD5E1;
      font-weight: 400;
    }
  }

  /* ADR-097: the position-TYPE heading. It carries numbers when a rule
     exists, so it cannot be styled as a passive label — but it stays lighter
     than the cluster row above and heavier than the positions beneath, which is
     what makes the three levels readable as a hierarchy rather than a list. */
  &.group {
    margin-top: 4px;

    .position.type {
      padding-left: calc(16px + var(--indent, 0px));
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.8px;
      color: #64748B;

      &::before {
        content: none;
      }
    }

    /* "follows PL&WE" — the fall-through said out loud. A blank heading would
       read as a rule of zero rather than the absence of a rule. */
    .num.follows {
      grid-column: 2 / 5;
      text-align: left;
      font-size: 11px;
      font-style: italic;
      font-weight: 400;
      color: #A3AFC0;
    }
  }

  /* A position sitting under a type heading is one level deeper */
  .position.nested {
    padding-left: calc(46px + var(--indent, 0px));
  }

  /* The add row carries controls rather than values, so it needs the height */
  &.add {
    padding-top: 8px;

    .position::before {
      content: '+';
      font-weight: 600;
    }
  }
`;

/*
 * The composer, fenced off from the rules above it.
 *
 * Empty number inputs sit in the same columns, at the same width, as the
 * numbers of a saved rule — so an unfilled form read as a configured
 * "0 + 0", which in this feature is a real and very different setting
 * (ADR-095 §3: null is not zero). The fence, the caption and the muted
 * palette say "nothing here is saved yet" before anyone reads a digit; the
 * inputs show "—" rather than "0" for the same reason.
 */
export const AddArea = styled.div`
  position: relative;
  margin: 10px 16px 0;
  padding: 8px 0 2px;

  /* The fence is drawn, not bordered, so its left end can start at the
     cluster's indent while the rows inside keep the grid's geometry —
     a shifted box would have moved the inputs out from under their columns. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: var(--indent, 0px);
    right: 0;
    border-top: 1px dashed #E2E8F0;
  }

  .caption {
    padding: 0 0 2px var(--indent, 0px);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #A3AFC0;
  }

  /* The rows keep the shared grid, so the controls stay under the columns
     they will eventually fill — the fence separates them, the alignment
     still shows which number is which. */
  ${OverrideRow} {
    padding-left: 0;
    padding-right: 0;
  }
`;
